// components/common/MyAccountSkeleton.jsx
import React from 'react';

const Skeleton = ({ className = '', variant = 'shimmer' }) => {
  const baseClasses = `relative overflow-hidden bg-gray-200 rounded ${className}`;
  
  const getVariantClasses = () => {
    switch (variant) {
      case 'shimmer':
        return 'before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_2s_ease-in-out_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/60 before:to-transparent';
      case 'pulse':
        return 'animate-pulse';
      case 'wave':
        return 'bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[size:200%_100%] animate-[wave_2s_linear_infinite]';
      default:
        return 'animate-pulse';
    }
  };

  return <div className={`${baseClasses} ${getVariantClasses()}`} />;
};

export const MyAccountSkeleton = () => (
  <div className="min-h-screen bg-gray-50">
    {/* Header Skeleton */}
    <div className="bg-white shadow-sm border-b sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-12">
          <Skeleton className="w-10 h-10 mr-4" variant="pulse" />
          <Skeleton className="h-7 w-32" variant="shimmer" />
        </div>
      </div>
    </div>

    {/* Profile Header Skeleton */}
    <div className="bg-gradient-to-r from-red-400 to-red-500 pt-8 pb-32 animate-pulse">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="relative inline-block">
            <div className="w-32 h-32 rounded-full bg-white/30 animate-pulse" />
            <div className="absolute bottom-0 right-0 w-10 h-10 bg-white/50 rounded-full animate-pulse" />
          </div>
          <div className="mt-4 space-y-2 flex flex-col items-center">
            <Skeleton className="h-8 w-48 bg-white/30" variant="wave" />
            <Skeleton className="h-5 w-64 bg-white/20" variant="shimmer" />
          </div>
        </div>
      </div>
    </div>

    {/* Content Skeleton */}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20">
      {/* Tabs Skeleton */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="flex border-b p-1">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="flex-1 opacity-0 animate-[fadeIn_0.3s_ease-out_forwards]"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              <Skeleton className="h-14 mx-1" variant="shimmer" />
            </div>
          ))}
        </div>
      </div>

      {/* Tab Content Skeleton */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <Skeleton className="h-7 w-48" variant="shimmer" />
          <Skeleton className="h-10 w-24 rounded-lg" variant="pulse" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { bg: 'bg-blue-50', delay: '0.1s' },
            { bg: 'bg-green-50', delay: '0.2s' },
            { bg: 'bg-purple-50', delay: '0.3s' },
            { bg: 'bg-orange-50', delay: '0.4s' }
          ].map((item, i) => (
            <div
              key={i}
              className="opacity-0 animate-[fadeInUp_0.5s_ease-out_forwards]"
              style={{ animationDelay: item.delay }}
            >
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" variant="pulse" />
                <div className={`p-1 rounded-lg ${item.bg}`}>
                  <Skeleton className="h-10 w-full bg-white/70" variant="shimmer" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Add animation styles
if (typeof document !== 'undefined' && !document.getElementById('account-skeleton-styles')) {
  const style = document.createElement('style');
  style.id = 'account-skeleton-styles';
  style.textContent = `
    @keyframes shimmer {
      to { transform: translateX(100%); }
    }
    @keyframes wave {
      0%, 100% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;
  document.head.appendChild(style);
}

export default MyAccountSkeleton;
